import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Collapse } from '@themesberg/react-bootstrap';
import { getTranslation, uploadFile } from '../../services/tablesService'; // Функция загрузки файла

const ModalForm = ({ show, handleClose, data, onSave, schema }) => {
    const [formData, setFormData] = useState(data || {});
    const [translatedLabels, setTranslatedLabels] = useState({});
    const [openSections, setOpenSections] = useState({});
    const [filesToUpload, setFilesToUpload] = useState({});

    // Подхватываем новое data
    useEffect(() => {
        setFormData(data || {});
        console.log('Form Data:', data || {});
    }, [data]);

    // <-- ВАЖНО: Инициализируем как объект (если по схеме не повторяющийся компонент)
    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            finansovye_pokazateli_kompanii: prev.finansovye_pokazateli_kompanii || {},
        }));
    }, [data]);

    // Перевод полей
    useEffect(() => {
        if (schema) {
            const translateFields = async () => {
                const translations = {};
                for (const key of Object.keys(schema.attributes)) {
                    // Переводим сам верхнеуровневый ключ
                    translations[key] = await getTranslation(key);

                    // Если поле - компонент, то переводим и "внутренние" поля
                    if (schema.attributes[key].type === 'component') {
                        const componentName = schema.attributes[key].component
                            .replace('components.', '')
                            .replace(/-/g, '_');

                        const componentDef = schema.components?.[componentName];
                        if (componentDef?.attributes) {
                            for (const subKey of Object.keys(componentDef.attributes)) {
                                translations[subKey] = await getTranslation(subKey);
                            }
                        }
                    }
                }
                console.log('Translated Labels:', translations);
                setTranslatedLabels(translations);
            };
            translateFields();
        }
    }, [schema]);

    // Изменение данных формы
    const handleInputChange = (e, parentKey, index) => {
        const { name, value } = e.target;
        setFormData((prevData) => {
            const updatedData = { ...prevData };

            // Если есть parentKey и index, значит поле внутри repeatable-компонента:
            if (index !== undefined) {
                if (!updatedData[parentKey]) {
                    updatedData[parentKey] = [];
                }
                if (!updatedData[parentKey][index]) {
                    updatedData[parentKey][index] = {};
                }
                updatedData[parentKey][index][name] = value;
            } else if (parentKey !== undefined) {
                // Если есть только parentKey (и нет index) — значит поле внутри НЕ repeatable-компонента
                if (!updatedData[parentKey]) {
                    updatedData[parentKey] = {};
                }
                updatedData[parentKey][name] = value;
            } else {
                // Иначе обычное (верхнеуровневое) поле
                updatedData[name] = value;
            }
            return updatedData;
        });
    };

    // Изменение файлов (корневой уровень)
    const handleFileChange = (e, parentKey, index) => {
        const files = e.target.files;
        setFilesToUpload((prevFiles) => {
            const updatedFiles = { ...prevFiles };
            if (index !== undefined) {
                if (!updatedFiles[parentKey]) {
                    updatedFiles[parentKey] = [];
                }
                updatedFiles[parentKey][index] = files;
            } else {
                updatedFiles[parentKey] = files;
            }
            return updatedFiles;
        });
    };

    // Изменение файлов для полей внутри компонента
    const handleFileComponentChange = (e, parentKey, index, fieldName) => {
        const files = e.target.files;
        setFilesToUpload((prevFiles) => {
            const updatedFiles = { ...prevFiles };
            if (index !== undefined) {
                if (!updatedFiles[parentKey]) {
                    updatedFiles[parentKey] = [];
                }
                if (!updatedFiles[parentKey][index]) {
                    updatedFiles[parentKey][index] = {};
                }
                updatedFiles[parentKey][index][fieldName] = files;
            } else {
                if (!updatedFiles[parentKey]) {
                    updatedFiles[parentKey] = {};
                }
                updatedFiles[parentKey][fieldName] = files;
            }
            return updatedFiles;
        });
    };

    // Загрузка файлов на сервер и получение их ID
    const uploadFilesAndGetIds = async () => {
        const uploadedFileIds = {};

        for (const key in filesToUpload) {
            if (Array.isArray(filesToUpload[key])) {
                // Массив (repeatable компонент)
                const filePromises = filesToUpload[key].map(async (fileObject) => {
                    const nestedPromises = Object.keys(fileObject).map(async (nestedKey) => {
                        const fileUploads = await Promise.all(
                            Object.keys(fileObject[nestedKey]).map(async (index) => {
                                const file = fileObject[nestedKey][index];
                                const uploadedFile = await uploadFile(file);
                                return uploadedFile.id;
                            })
                        );
                        return { [nestedKey]: fileUploads };
                    });
                    return Object.assign({}, ...(await Promise.all(nestedPromises)));
                });
                uploadedFileIds[key] = await Promise.all(filePromises);
            } else {
                // Обычное (не repeatable) поле
                const fileUploads = await Promise.all(
                    Object.keys(filesToUpload[key]).map(async (index) => {
                        const file = filesToUpload[key][index];
                        const uploadedFile = await uploadFile(file);
                        return uploadedFile.id;
                    })
                );
                uploadedFileIds[key] = fileUploads;
            }
        }
        return uploadedFileIds;
    };

    // Submit формы
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            console.log('Данные формы перед загрузкой файлов:', JSON.stringify(formData, null, 2));

            const fileIds = await uploadFilesAndGetIds();
            console.log('Идентификаторы загруженных файлов:', JSON.stringify(fileIds, null, 2));

            const updatedData = { ...formData };
            for (const key in fileIds) {
                if (Array.isArray(updatedData[key])) {
                    updatedData[key].forEach((item, index) => {
                        const fileKey = Object.keys(fileIds[key][index])[0];
                        item[fileKey] = fileIds[key][index][fileKey];
                    });
                } else {
                    updatedData[key] = fileIds[key];
                }
            }

            console.log('Обновлённые данные для сохранения:', JSON.stringify(updatedData, null, 2));

            if (onSave) {
                await onSave(updatedData);
                console.log('Данные переданы в onSave:', JSON.stringify(updatedData, null, 2));
            }
        } catch (error) {
            console.error(
                'Ошибка при сохранении данных:',
                error.response ? error.response.data : error.message
            );
        }
    };

    // Открытие/закрытие секций для компонент
    const toggleSection = (key) => {
        setOpenSections((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    // Добавление нового повторяющегося компонента
    const addComponentField = (key) => {
        setFormData((prevData) => ({
            ...prevData,
            [key]: [...(prevData[key] || []), {}],
        }));
    };

    // Рендер компонента (repeatable или нет)
    const renderComponent = (key, component, repeatable) => {
        return (
            <div key={key}>
                <h5>
                    <Button
                        onClick={() => toggleSection(key)}
                        aria-controls={`${key}-collapse`}
                        aria-expanded={openSections[key]}
                    >
                        {translatedLabels[key] || key}
                    </Button>
                </h5>
                <Collapse in={openSections[key]}>
                    <div id={`${key}-collapse`}>
                        {repeatable ? (
                            <div>
                                {formData[key]?.map((item, index) => (
                                    <div key={index}>
                                        {renderComponentFields(component, key, index)}
                                    </div>
                                ))}
                                <Button onClick={() => addComponentField(key)}>
                                    Добавить {translatedLabels[key] || key}
                                </Button>
                            </div>
                        ) : (
                            // Если не repeatable, просто рендерим один объект
                            renderComponentFields(component, key)
                        )}
                    </div>
                </Collapse>
            </div>
        );
    };

    // Рендер полей внутри компонента
    const renderComponentFields = (component, parentKey, index) => {
        if (!component || !component.attributes) return null;
        const fields = component.attributes;

        return Object.keys(fields).map((key) => {
            const field = fields[key];
            switch (field.type) {
                case 'string':
                case 'integer':
                case 'float':
                case 'decimal':
                case 'date':
                    return (
                        <Form.Group key={key} controlId={key}>
                            <Form.Label>{translatedLabels[key] || key}</Form.Label>
                            <Form.Control
                                type={
                                    field.type === 'date'
                                        ? 'date'
                                        : field.type === 'integer' ||
                                        field.type === 'decimal' ||
                                        field.type === 'float'
                                            ? 'number'
                                            : 'text'
                                }
                                name={key}
                                defaultValue={
                                    index !== undefined
                                        ? (formData[parentKey]?.[index] || {})[key] ?? ''
                                        : formData[parentKey]?.[key] ?? ''
                                }
                                onChange={(e) => handleInputChange(e, parentKey, index)}
                                required={field.required}
                                step={
                                    field.type === 'decimal' || field.type === 'float'
                                        ? 'any'
                                        : undefined
                                }
                            />
                        </Form.Group>
                    );
                case 'media':
                    return (
                        <Form.Group key={key} controlId={key}>
                            <Form.Label>{translatedLabels[key] || key}</Form.Label>
                            <Form.Control
                                type="file"
                                name={key}
                                multiple={field.multiple}
                                onChange={(e) => handleFileComponentChange(e, parentKey, index, key)}
                                required={field.required}
                            />
                        </Form.Group>
                    );
                case 'enumeration':
                    return (
                        <Form.Group key={key} controlId={key}>
                            <Form.Label>{translatedLabels[key] || key}</Form.Label>
                            <Form.Select
                                name={key}
                                value={
                                    index !== undefined
                                        ? (formData[parentKey]?.[index] || {})[key] || ''
                                        : formData[parentKey]?.[key] || ''
                                }
                                onChange={(e) => handleInputChange(e, parentKey, index)}
                                required={field.required}
                            >
                                <option value="">Выберите</option>
                                {field.enum.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    );
                default:
                    return null;
            }
        });
    };

    // Рендер верхнеуровневых полей
    const renderFields = () => {
        if (!schema) return null;

        return Object.keys(schema.attributes).map((key) => {
            if (key === 'createdBy' || key === 'updatedBy') return null;
            const field = schema.attributes[key];

            switch (field.type) {
                case 'string':
                    return (
                        <Form.Group key={key} controlId={key}>
                            <Form.Label>{translatedLabels[key] || key}</Form.Label>
                            <Form.Control
                                type="text"
                                name={key}
                                value={formData[key] || ''}
                                onChange={handleInputChange}
                                required={field.required}
                            />
                        </Form.Group>
                    );
                case 'integer':
                case 'float':
                case 'decimal':
                    return (
                        <Form.Group key={key} controlId={key}>
                            <Form.Label>{translatedLabels[key] || key}</Form.Label>
                            <Form.Control
                                type="number"
                                name={key}
                                value={formData[key] || ''}
                                onChange={handleInputChange}
                                required={field.required}
                                min={field.min}
                                step={field.type === 'decimal' || field.type === 'float' ? 'any' : '1'}
                            />
                        </Form.Group>
                    );
                case 'text':
                    return (
                        <Form.Group key={key} controlId={key}>
                            <Form.Label>{translatedLabels[key] || key}</Form.Label>
                            <Form.Control
                                as="textarea"
                                name={key}
                                value={formData[key] || ''}
                                onChange={handleInputChange}
                                required={field.required}
                            />
                        </Form.Group>
                    );
                case 'media':
                    return (
                        <Form.Group key={key} controlId={key}>
                            <Form.Label>{translatedLabels[key] || key}</Form.Label>
                            <Form.Control
                                type="file"
                                name={key}
                                multiple={field.multiple}
                                onChange={(e) => handleFileChange(e, key)}
                                required={field.required}
                            />
                        </Form.Group>
                    );
                case 'boolean':
                    return (
                        <Form.Group key={key} controlId={key}>
                            <Form.Check
                                type="checkbox"
                                label={translatedLabels[key] || key}
                                name={key}
                                checked={formData[key] || false}
                                onChange={(e) =>
                                    handleInputChange({
                                        target: { name: key, value: e.target.checked },
                                    })
                                }
                            />
                        </Form.Group>
                    );
                case 'enumeration':
                    return (
                        <Form.Group key={key} controlId={key}>
                            <Form.Label>{translatedLabels[key] || key}</Form.Label>
                            <Form.Select
                                name={key}
                                value={formData[key] || ''}
                                onChange={handleInputChange}
                                required={field.required}
                            >
                                <option value="">Выберите</option>
                                {field.enum.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    );
                case 'date':
                    return (
                        <Form.Group key={key} controlId={key}>
                            <Form.Label>{translatedLabels[key] || key}</Form.Label>
                            <Form.Control
                                type="date"
                                name={key}
                                value={formData[key] || ''}
                                onChange={handleInputChange}
                                required={field.required}
                            />
                        </Form.Group>
                    );
                case 'component':
                    // Проверяем, есть ли описание компонента
                    const componentName = field.component
                        .replace('components.', '')
                        .replace(/-/g, '_');
                    const component = schema.components?.[componentName];
                    if (component) {
                        return renderComponent(key, component, field.repeatable);
                    }
                    return null;
                default:
                    return null;
            }
        });
    };

    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>{data ? 'Редактировать запись' : 'Создать запись'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleSubmit}>
                    {renderFields()}
                    <Button type="submit" variant="primary">
                        Сохранить
                    </Button>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default ModalForm;
